import { z } from "zod";
import { resolveDataMode, type DataMode } from "./data-mode";

const schema = z.object({
  VITE_APP_NAME: z.string().default("Cruzeiro Life"),
  VITE_DATA_MODE: z.enum(["demo", "persistent"]).optional(),
  VITE_USE_MOCKS: z.enum(["true", "false"]).optional(),
  // A relative path is required when the pilot is served behind the same
  // reverse proxy as the mock API. Keep absolute URLs available for local
  // development, but reject protocol-relative and malformed values.
  VITE_API_URL: z
    .string()
    .refine(
      (value) => {
        if (value.startsWith("/")) return !value.startsWith("//");
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      },
      { message: "VITE_API_URL must be an absolute URL or a safe relative path." },
    )
    .default("http://localhost:8787"),
  VITE_SUPABASE_URL: z.string().optional(),
  VITE_SUPABASE_ANON_KEY: z.string().optional(),
});

export interface AppEnv {
  appName: string;
  dataMode: DataMode;
  useMocks: boolean;
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

export function resolveAppEnv(input: Record<string, unknown>): AppEnv {
  const parsed = schema.parse(input);
  const dataMode = resolveDataMode({
    dataMode: parsed.VITE_DATA_MODE,
    useMocks: parsed.VITE_USE_MOCKS,
  });
  const supabaseUrl = parsed.VITE_SUPABASE_URL;
  const supabaseAnonKey = parsed.VITE_SUPABASE_ANON_KEY;

  if (dataMode === "persistent" && (!supabaseUrl || !supabaseAnonKey)) {
    throw new Error(
      "VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórios no modo persistent.",
    );
  }

  return {
    appName: parsed.VITE_APP_NAME,
    dataMode,
    useMocks: dataMode === "demo",
    apiUrl: parsed.VITE_API_URL,
    supabaseUrl: supabaseUrl ?? "http://127.0.0.1:54321",
    supabaseAnonKey: supabaseAnonKey ?? "local-anon-key",
  };
}

export const env = resolveAppEnv(import.meta.env);
